import { AnyAction } from 'redux';
import * as actionTypes from '../../../../models/actionTypes';
import {
  IRequestHistoryState,
  initialRequestHistoryState,
} from '../../../../models/reduxState';
import { constructUserPhone, deepCopyProperty } from '../../../../utilities';
import { ExtensionAttributeKeys, FreelanceRequest } from '../../models/requestModels';
import { updateDraftRequest } from './modalitySectionReducer';

// For non-Agave (InApp) and Outlook.com
const handleSupportDataGrabSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  return {
    ...state,
    isSearchRequestsEnabledConsumer: !!action.supportSettingsConfig && action.supportSettingsConfig.IsSearchRequestsEnabledConsumer,
    isSearchRequestsEnabledCommercial: !!action.supportSettingsConfig && action.supportSettingsConfig.IsSearchRequestsEnabledCommercial,
    isSearchRequestsEnabledBA: !!action.supportSettingsConfig && action.supportSettingsConfig.IsSearchRequestsEnabledBA,
    showRequestSubNav: !!action.supportSettingsConfig && action.supportSettingsConfig.ShowRequestSubNav,
  };
};

// For all entry points other than non-Agave (InApp) and Outlook.com
const handleSocDataGrabSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  return {
    ...state,
    isSearchRequestsEnabledConsumer: !!action.supportSettingsConfig && action.supportSettingsConfig.IsSearchRequestsEnabledConsumer,
    isSearchRequestsEnabledCommercial: !!action.supportSettingsConfig && action.supportSettingsConfig.IsSearchRequestsEnabledCommercial,
    isSearchRequestsEnabledBA: !!action.supportSettingsConfig && action.supportSettingsConfig.IsSearchRequestsEnabledBA,
    showRequestSubNav: !!action.supportSettingsConfig && action.supportSettingsConfig.ShowRequestSubNav,
  };
};

const handleSearchRequests = (
  state: IRequestHistoryState = initialRequestHistoryState,
  _action: AnyAction
): IRequestHistoryState => {
  return {
    ...state,
    isSearchingRequests: true,
  };
};

const handleSearchRequestsSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  try {
    if (!!action && !!action.requests && action.requests.length > 0) {
      action.requests.sort((requestA: FreelanceRequest, requestB: FreelanceRequest)=> new Date(requestB.CreateDateTime).getTime() - new Date(requestA.CreateDateTime).getTime());
    }
  } catch(e) {
    console.log("error while sorting requests: " + e)
  }
  return {
    ...state,
    isSearchingRequests: false,
    allRequests: action.requests,
  };
};

const handleSearchRequestsError = (
  state: IRequestHistoryState = initialRequestHistoryState,
  _action: AnyAction
): IRequestHistoryState => {
  return {
    ...state,
    isSearchingRequests: false,
    allRequests: null,
  };
};

const handleGetRequestSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  let index = 0;
  const allRequests = state.allRequests ? [...state.allRequests] : [];
  const updatedRequest = allRequests.find((request: FreelanceRequest, key: number) => {
    index = key;
    return (
      !!request &&
      !!request.RequestId &&
      !!action.request &&
      !!action.request.RequestId &&
      request.RequestId!.toUpperCase() === action.request!.RequestId!.toUpperCase());
  });
  if (updatedRequest) {
    allRequests[index] = action.request;
  } else {
    allRequests.push(action.request);
  }
  return {
    ...state,
    allRequests,
  };
};

const handleCreateRequestSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  const allRequests = state.allRequests ? [...state.allRequests] : [];
  if (action.request) {
    allRequests.unshift(action.request);
  }
  return {
    ...state,
    allRequests: allRequests,
  };
};

// Only for RequestDetails edit scenario
const handleUpdateAttachmentsSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  const updatedState = { ...state };
  if (action.dirtyRequest) {
    const dirtyRequest: FreelanceRequest = action.dirtyRequest;
    const attachments = dirtyRequest.Attachments;
    const allRequests = [...updatedState.allRequests];
    if (allRequests) {
      const idx = allRequests.findIndex((r: FreelanceRequest) => r.RequestId === dirtyRequest.RequestId );
      if (idx !== -1) {
        allRequests[idx] = updateDraftRequest(allRequests[idx], { Attachments: attachments });
      }
      updatedState.allRequests = allRequests;
    }
  }
  return updatedState;
};

// Only for RequestDetails edit scenario
const handleAddNoteSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  const updatedState = { ...state };
  if (action.dirtyRequest) {
    const dirtyRequest: FreelanceRequest = action.dirtyRequest;
    const notes = dirtyRequest.Notes;
    // areNotesValidSelector should take care of if the lastNote is null, checking here as well
    if (!!notes && notes.length > 1) {
      const lastNote = notes[notes.length -1];
      lastNote.Time = new Date().toISOString();
    }
    const allRequests = [...updatedState.allRequests];
    if (allRequests) {
      const idx = allRequests.findIndex((r: FreelanceRequest) => r.RequestId === dirtyRequest.RequestId );
      if (idx !== -1) {
        allRequests[idx] = updateDraftRequest(allRequests[idx], { Notes: notes });
      }
      updatedState.allRequests = allRequests;
    }
  }
  return updatedState;
};

const handleUpdateContactsSuccess = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  const updatedState = { ...state };
  if (action.dirtyRequest) {
    const dirtyRequest: FreelanceRequest = action.dirtyRequest;
    /**
     * We were not separating dialingCode from phone number, adding null check for tickets created before change
     * UI changes only updates UserCountryDialingCode/UserPhoneWithoutDialingCode but not UserPhone. When UpdateContacts succeeds, manually update UserPhone here as well.
     */
    dirtyRequest.UserPhone = constructUserPhone(dirtyRequest);
    const allRequests = [...updatedState.allRequests];
    const userPhone = dirtyRequest.UserPhone;
    const userEmail = dirtyRequest.UserEmail;
    const userCountryDialingCode = dirtyRequest.UserCountryDialingCode;
    const userPhoneWithoutDialingCode = dirtyRequest.UserPhoneWithoutDialingCode;
    const userPhoneDialingCodeIndex = dirtyRequest.UserPhoneDialingCodeIndex;
    const customerContacts = deepCopyProperty(dirtyRequest.CustomerContacts);
    if (allRequests) {
      const idx = allRequests.findIndex(
        (r: FreelanceRequest) => r.RequestId === dirtyRequest.RequestId
      );
      if (idx !== -1) {
        const request = allRequests[idx];
        const extensionAttributes = !!request.ExtensionAttributes
          ? { ...request.ExtensionAttributes }
          : null;
        if (!!extensionAttributes) {
          extensionAttributes[ExtensionAttributeKeys.IsoCallingCode] =
          dirtyRequest.TwoDigitCountryCode;
        }
        allRequests[idx] = updateDraftRequest(request, {
          UserPhone: userPhone,
          UserEmail: userEmail,
          UserCountryDialingCode: userCountryDialingCode,
          UserPhoneWithoutDialingCode: userPhoneWithoutDialingCode,
          UserPhoneDialingCodeIndex: userPhoneDialingCodeIndex,
          CustomerContacts: customerContacts,
          TwoDigitCountryCode: dirtyRequest.TwoDigitCountryCode,
          ExtensionAttributes: extensionAttributes,
        });
      }
      updatedState.allRequests = allRequests;
    }
  }
  return updatedState;
};

export const RequestHistoryReducer = (
  state: IRequestHistoryState = initialRequestHistoryState,
  action: AnyAction
): IRequestHistoryState => {
  switch (action.type) {
    case actionTypes.GET_EMERALD_SUPPORTCONFIG_SUCCESS:
      return handleSupportDataGrabSuccess(state, action);
    case actionTypes.GET_EMERALD_CONFIG_SUCCESS:
      return handleSocDataGrabSuccess(state, action);
    case actionTypes.SEARCH_REQUESTS:
      return handleSearchRequests(state, action);
    case actionTypes.SEARCH_REQUESTS_SUCCESS:
      return handleSearchRequestsSuccess(state, action);
    case actionTypes.SEARCH_REQUESTS_ERROR:
      return handleSearchRequestsError(state, action);
    case actionTypes.GET_REQUEST_SUCCESS:
      return handleGetRequestSuccess(state, action);
    case actionTypes.CREATE_REQUEST_SUCCESS:
      return handleCreateRequestSuccess(state, action);
    case actionTypes.UPDATE_ATTACHMENTS_SUCCESS:
      return handleUpdateAttachmentsSuccess(state, action);
    case actionTypes.ADD_NOTE_SUCCESS:
      return handleAddNoteSuccess(state, action);
    case actionTypes.UPDATE_CONTACTS_SUCCESS:
      return handleUpdateContactsSuccess(state, action);
    default:
      return state;
  }
};
